import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Search, CalendarDays, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function Cell({ s }) {
  if (s === "P") {
    return (
      <div className="grid h-6 w-6 place-items-center rounded-full bg-emerald-500 text-[10px] font-semibold text-white" data-testid="cal-cell-p">
        P
      </div>
    );
  }
  if (s === "A") {
    return (
      <div className="grid h-6 w-6 place-items-center rounded-full bg-rose-500 text-[10px] font-semibold text-white" data-testid="cal-cell-a">
        A
      </div>
    );
  }
  if (s === "H") {
    return (
      <div className="grid h-6 w-6 place-items-center rounded-full bg-amber-400 text-[10px] font-semibold text-white" data-testid="cal-cell-h">
        H
      </div>
    );
  }
  return <div className="grid h-6 w-6 place-items-center text-[10px] text-slate-300">·</div>;
}

/**
 * PMC Worker Attendance Calendar — modal widget.
 * Renders a monthly grid (1..31) per assigned park, showing each worker's
 * daily Present (P) / Absent (A) / Holiday (H) / Future ("-") status.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 */
export function WorkerAttendanceCalendarDialog({ open, onClose }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1..12
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");

  const monthLabel = useMemo(() => {
    const m = new Date(year, month - 1, 1);
    return m.toLocaleString("en-IN", { month: "long", year: "numeric" });
  }, [year, month]);

  const monthParam = `${year}-${String(month).padStart(2, "0")}`;

  const fetchData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const r = await api.get("/pmc/worker-attendance-calendar", { params: { month: monthParam } });
      setData(r.data);
    } catch {
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  }, [monthParam, open]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const moveMonth = (delta) => {
    let mm = month + delta;
    let yy = year;
    if (mm < 1) { mm = 12; yy -= 1; }
    if (mm > 12) { mm = 1; yy += 1; }
    setMonth(mm); setYear(yy);
  };

  const days = data?.days || [];

  const filteredParks = useMemo(() => {
    if (!data?.parks) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.parks;
    return data.parks
      .map((p) => {
        const parkMatches =
          (p.name || "").toLowerCase().includes(q) ||
          (p.code || "").toLowerCase().includes(q) ||
          (p.zone || "").toLowerCase().includes(q);
        const workers = (p.workers || []).filter((w) =>
          (w.name || "").toLowerCase().includes(q) ||
          (w.employee_code || "").toLowerCase().includes(q)
        );
        if (parkMatches) return p;
        if (workers.length) return { ...p, workers };
        return null;
      })
      .filter(Boolean);
  }, [data, search]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-5xl p-0 overflow-hidden flex flex-col max-h-[92vh] w-[95vw] sm:w-full sm:max-h-[88vh]"
        data-testid="worker-cal-dialog"
      >
        {/* Header band */}
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 px-5 py-4 text-white relative flex-shrink-0">
          <DialogHeader className="space-y-1">
            <DialogTitle className="flex items-center gap-2 text-white">
              <CalendarDays className="h-5 w-5" />
              Worker Attendance Calendar
            </DialogTitle>
            <div className="text-xs text-emerald-50/90">Your assigned parks</div>
          </DialogHeader>
          <button
            type="button"
            onClick={() => onClose?.()}
            className="absolute right-3 top-3 rounded-full p-1 text-white/90 hover:bg-white/10"
            aria-label="Close"
            data-testid="worker-cal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Month nav */}
        <div className="px-5 pt-4 flex-shrink-0">
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => moveMonth(-1)}
              data-testid="worker-cal-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-[180px] text-center text-base font-semibold" data-testid="worker-cal-month">
              {monthLabel}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => moveMonth(1)}
              data-testid="worker-cal-next"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="px-5 pt-3 flex-shrink-0">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              className="pl-9"
              placeholder="Search by park name, worker name, or zone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="worker-cal-search"
            />
          </div>
        </div>

        {/* Body — mobile-first per-worker cards */}
        <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-4 pt-3 space-y-3 sm:px-5 sm:pb-5">
          {loading ? (
            <div className="text-center text-sm text-muted-foreground py-12">Loading…</div>
          ) : filteredParks.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-12">
              No data for this month
            </div>
          ) : (
            filteredParks.map((p) => (
              <div key={p.park_id} className="rounded-lg border bg-card overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-slate-50 px-3 py-2">
                  <div className="text-sm font-semibold" data-testid={`worker-cal-park-${p.code}`}>
                    {p.name}
                  </div>
                  {(p.zone || p.division) ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200 text-[10px]">
                      {p.zone ? `Zone ${p.zone}` : ""}{p.division ? ` · Div ${p.division}` : ""}
                    </Badge>
                  ) : null}
                </div>
                {p.workers.length === 0 ? (
                  <div className="px-3 py-4 text-center text-xs text-slate-400">No workers</div>
                ) : (
                  <div className="divide-y">
                    {p.workers.map((w) => (
                      <div key={w.employee_id} className="px-3 py-2.5" data-testid={`worker-cal-row-${w.employee_code}`}>
                        {/* Header row: name, category, total P */}
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-slate-700">{w.name}</div>
                            <div className="text-[10px] uppercase tracking-wide text-slate-500">{w.category || "—"}</div>
                          </div>
                          <div
                            className="shrink-0 rounded-md bg-emerald-100 px-2 py-1 text-center text-[10px] font-semibold text-emerald-800"
                            data-testid={`worker-cal-total-${w.employee_code}`}
                          >
                            <div className="text-[9px] uppercase tracking-wide text-emerald-700">Total P</div>
                            <div className="text-base leading-none">{w.totals?.P ?? 0}</div>
                          </div>
                        </div>
                        {/* Day strip — horizontally scrollable on small screens */}
                        <div className="-mx-1 overflow-x-auto pb-1">
                          <div className="flex gap-1.5 px-1">
                            {(w.statuses || []).map((s, i) => {
                              const dayNum = parseInt(days[i].slice(-2), 10);
                              return (
                                <div key={i} className="flex shrink-0 flex-col items-center gap-1">
                                  <div className="text-[9px] text-slate-400">{dayNum}</div>
                                  <Cell s={s} />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
